import { useState } from "react";
import { motion } from "framer-motion";
import { BookOpen, FileText, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ShareMenu } from "@/components/ShareMenu";
import { SaveOfflineButton } from "@/components/SaveOfflineButton";
import { coverThumbUrl } from "@/lib/appresse";
import { useAuth } from "@/lib/auth";
import { useReader } from "@/components/Layout";

export const EditionCard = ({ edition, index = 0 }) => {
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const { user, isFavorite, toggleFavorite } = useAuth();
  const { openReader } = useReader();
  const fav = isFavorite(edition);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.4) }}
      data-testid={`edition-card-${edition.id}`}
      className="edition-hover group relative bg-card border border-border rounded-2xl overflow-hidden shadow-[0_10px_28px_-6px_rgba(0,0,0,0.38)] transition-shadow duration-300 hover:shadow-[0_18px_44px_-8px_rgba(0,0,0,0.55)] hover:border-primary/40"
    >
      <button onClick={() => openReader(edition)} data-testid={`edition-read-button-${edition.id}`}
        className="block w-full relative aspect-[3/4] bg-muted overflow-hidden" aria-label={`Lire ${edition.title}`}>
        <div className="absolute top-0 inset-x-0 h-1 bg-white z-10" />
        {edition.has_cover && !imgError ? (
          <>
            {edition.thumb_blur && !imgLoaded && (
              <img src={edition.thumb_blur} alt="" aria-hidden
                className="absolute inset-0 w-full h-full object-cover object-top scale-110 blur-lg" />
            )}
            <img src={coverThumbUrl(edition.id)} alt={edition.title}
              onError={() => setImgError(true)} onLoad={() => setImgLoaded(true)}
              loading={index < 4 ? "eager" : "lazy"}
              fetchPriority={index < 4 ? "high" : "auto"}
              decoding="async"
              className={`relative w-full h-full object-cover object-top group-hover:scale-[1.03] transition-all duration-300 ${imgLoaded ? "opacity-100" : "opacity-0"}`} />
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 p-4 paper-texture">
            <FileText className="w-10 h-10 text-primary" />
            <span className="font-serif font-bold text-center text-sm leading-tight line-clamp-3">{edition.title}</span>
          </div>
        )}
      </button>

      {user && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggleFavorite(edition.id, edition.title); }}
          data-testid={`edition-fav-button-${edition.id}`}
          aria-pressed={fav}
          aria-label={fav ? "Retirer des favoris" : "Ajouter aux favoris"}
          className="absolute top-2.5 right-2.5 z-20 w-9 h-9 rounded-full flex items-center justify-center bg-black/35 backdrop-blur-sm text-white shadow-sm transition-all hover:bg-black/55 active:scale-90"
        >
          <Heart className={`w-[18px] h-[18px] transition-all ${fav ? "fill-red-500 text-red-500 scale-110" : "text-white"}`} />
        </button>
      )}

      <div className="p-3 sm:p-3.5">
        <div className="flex items-center gap-1.5 min-w-0">
          <Button size="sm" className="flex-1 min-w-0 gap-1.5 px-2" onClick={() => openReader(edition)} data-testid={`edition-open-button-${edition.id}`}>
            <BookOpen className="w-4 h-4 shrink-0" /> <span className="truncate">Lire</span>
          </Button>
          {user && (
            <>
              <SaveOfflineButton edition={edition} />
              <ShareMenu edition={edition} size="icon" />
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};
